import React, { useState, useEffect, useRef } from 'react';
import { Phone, Mail, Users, Calendar, MoreVertical, Check, X, Clock, UserMinus, Edit3, MessageCircle, Bell, Copy, FileText, History, User } from 'lucide-react';
import { GuestEvent, CompanionGuestEvent, guestAPI } from '../../services/guestAPI';
import { useAppSelector } from '../../store/hooks';
import { useGuestDisplayName } from '../../hooks/useGuestDisplayName';
import { formatGuestEventDisplayName } from '../../utils/guestNameUtils';
import HistoryModal from './HistoryModal';
import { logError } from '../../utils/errorLogger';
import { GuestEventHistoryEntry } from '../../services/analyticsService';

interface GuestCardProps {
  guest: GuestEvent;
  eventId: string;
  onStatusUpdate: (guestId: number, status: GuestEvent['status']) => void;
  onDelete: (guestId: number, guestName: string) => void;
  onSelect?: (guestId: string, selected: boolean) => void;
  onEditCompanions?: (guestId: number, guest: GuestEvent) => void;
  onEditComments?: (guest: GuestEvent) => void;
  onSendReminder?: (guestId: string) => void;
  onError?: (message: string) => void;
  selected?: boolean;
  compact?: boolean;
  eventHasEnded?: boolean;
}

const StatusBadge = React.forwardRef<HTMLButtonElement, { status: GuestEvent['status']; onClick?: () => void }>(
  ({ status, onClick }, ref) => {
    const { theme } = useAppSelector((state) => state.ui);
    
    // Debug logging for status changes (disabled to reduce console spam)
    // console.log('🏷️ STATUS BADGE - Rendering with status:', status);
    
    const statusConfig = {
      pending: {
        label: 'Pending',
        color: theme === 'dark' 
          ? 'bg-yellow-900/30 text-yellow-300 border-yellow-700/50'
          : 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: Clock
      },
      accepted: {
        label: 'Confirmed',
        color: theme === 'dark'
          ? 'bg-green-900/30 text-green-300 border-green-700/50'
          : 'bg-green-100 text-green-800 border-green-200',
        icon: Check
      },
      rejected: {
        label: 'Declined',
        color: theme === 'dark'
          ? 'bg-red-900/30 text-red-300 border-red-700/50'
          : 'bg-red-100 text-red-800 border-red-200',
        icon: X
      },
      ignored: {
        label: 'No Response',
        color: theme === 'dark'
          ? 'bg-gray-700 text-gray-300 border-gray-600'
          : 'bg-gray-100 text-gray-800 border-gray-200',
        icon: UserMinus
      }
    };

    const config = statusConfig[status];
    const Icon = config.icon;

    return (
      <button
        ref={ref}
        onClick={onClick}
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border transition-all duration-200 hover:scale-105 ${config.color}`}
      >
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </button>
    );
  }
);
StatusBadge.displayName = 'StatusBadge';

const WhatsAppBadge: React.FC<{ whatsappStatus?: string; whatsappSentAt?: string }> = ({ whatsappStatus, whatsappSentAt }) => {
  const { theme } = useAppSelector((state) => state.ui);
  
  if (!whatsappStatus) return null;
  
  // Define status configurations with colors and icons
  const statusConfig: Record<string, { 
    label: string; 
    bgDark: string; 
    bgLight: string; 
    textDark: string; 
    textLight: string; 
    borderDark: string; 
    borderLight: string;
    icon: typeof MessageCircle;
  }> = {
    'queued': {
      label: 'Queued',
      bgDark: 'bg-blue-900/30',
      bgLight: 'bg-blue-100',
      textDark: 'text-blue-300',
      textLight: 'text-blue-800',
      borderDark: 'border-blue-700/50',
      borderLight: 'border-blue-200',
      icon: Clock
    },
    'sending': {
      label: 'Sending',
      bgDark: 'bg-blue-900/30',
      bgLight: 'bg-blue-100',
      textDark: 'text-blue-300',
      textLight: 'text-blue-800',
      borderDark: 'border-blue-700/50',
      borderLight: 'border-blue-200',
      icon: MessageCircle
    },
    'sent': {
      label: 'Sent',
      bgDark: 'bg-green-900/30',
      bgLight: 'bg-green-100',
      textDark: 'text-green-300',
      textLight: 'text-green-800',
      borderDark: 'border-green-700/50',
      borderLight: 'border-green-200',
      icon: MessageCircle
    },
    'delivered': {
      label: 'Delivered',
      bgDark: 'bg-green-900/30',
      bgLight: 'bg-green-100',
      textDark: 'text-green-400',
      textLight: 'text-green-800',
      borderDark: 'border-green-700/50',
      borderLight: 'border-green-200',
      icon: Check
    },
    'read': {
      label: 'Read',
      bgDark: 'bg-green-900/30',
      bgLight: 'bg-green-100',
      textDark: 'text-green-400',
      textLight: 'text-green-900',
      borderDark: 'border-green-700/50',
      borderLight: 'border-green-200',
      icon: Check
    },
    'failed': {
      label: 'Failed',
      bgDark: 'bg-red-900/30',
      bgLight: 'bg-red-100',
      textDark: 'text-red-300',
      textLight: 'text-red-800',
      borderDark: 'border-red-700/50',
      borderLight: 'border-red-200',
      icon: X
    },
    'undelivered': {
      label: 'Undelivered',
      bgDark: 'bg-red-900/30',
      bgLight: 'bg-red-100',
      textDark: 'text-red-300',
      textLight: 'text-red-800',
      borderDark: 'border-red-700/50',
      borderLight: 'border-red-200',
      icon: X
    }
  };
  
  const config = statusConfig[whatsappStatus] || statusConfig['sent'];
  const Icon = config.icon;

  return (
    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
      theme === 'dark'
        ? `${config.bgDark} ${config.textDark} ${config.borderDark}`
        : `${config.bgLight} ${config.textLight} ${config.borderLight}`
    }`}>
      <Icon className="h-3 w-3 mr-1" />
      {config.label}
    </div>
  );
};

const ImageMessageBadge: React.FC<{ imageMessageStatus?: string | null }> = ({ imageMessageStatus }) => {
  const { theme } = useAppSelector((state) => state.ui);
  if (!imageMessageStatus) return null;
  const statusConfig: Record<string, { label: string; bgDark: string; bgLight: string; textDark: string; textLight: string; borderDark: string; borderLight: string; icon: typeof MessageCircle }> = {
    queued: { label: 'Image: Queued', bgDark: 'bg-blue-900/30', bgLight: 'bg-blue-100', textDark: 'text-blue-300', textLight: 'text-blue-800', borderDark: 'border-blue-700/50', borderLight: 'border-blue-200', icon: Clock },
    sending: { label: 'Image: Sending', bgDark: 'bg-blue-900/30', bgLight: 'bg-blue-100', textDark: 'text-blue-300', textLight: 'text-blue-800', borderDark: 'border-blue-700/50', borderLight: 'border-blue-200', icon: MessageCircle },
    sent: { label: 'Image: Sent', bgDark: 'bg-green-900/30', bgLight: 'bg-green-100', textDark: 'text-green-300', textLight: 'text-green-800', borderDark: 'border-green-700/50', borderLight: 'border-green-200', icon: MessageCircle },
    delivered: { label: 'Image: Delivered', bgDark: 'bg-green-900/30', bgLight: 'bg-green-100', textDark: 'text-green-400', textLight: 'text-green-800', borderDark: 'border-green-700/50', borderLight: 'border-green-200', icon: Check },
    read: { label: 'Image: Read', bgDark: 'bg-green-900/30', bgLight: 'bg-green-100', textDark: 'text-green-400', textLight: 'text-green-900', borderDark: 'border-green-700/50', borderLight: 'border-green-200', icon: Check },
    failed: { label: 'Image: Failed', bgDark: 'bg-red-900/30', bgLight: 'bg-red-100', textDark: 'text-red-300', textLight: 'text-red-800', borderDark: 'border-red-700/50', borderLight: 'border-red-200', icon: X },
    undelivered: { label: 'Image: Undelivered', bgDark: 'bg-red-900/30', bgLight: 'bg-red-100', textDark: 'text-red-300', textLight: 'text-red-800', borderDark: 'border-red-700/50', borderLight: 'border-red-200', icon: X },
  };
  const config = statusConfig[imageMessageStatus] || statusConfig['sent'];
  const Icon = config.icon;
  return (
    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
      theme === 'dark' ? `${config.bgDark} ${config.textDark} ${config.borderDark}` : `${config.bgLight} ${config.textLight} ${config.borderLight}`
    }`}>
      <Icon className="h-3 w-3 mr-1" />
      {config.label}
    </div>
  );
};

const StatusUpdateMenu: React.FC<{
  currentStatus: GuestEvent['status'];
  onStatusChange: (status: GuestEvent['status']) => void;
  onClose: () => void;
  position?: 'left' | 'right';
  align?: 'top' | 'bottom';
}> = ({ currentStatus, onStatusChange, onClose, position = 'right', align = 'bottom' }) => {
  const { theme } = useAppSelector((state) => state.ui);
  
  const statusOptions: Array<{ value: GuestEvent['status']; label: string; color: string; icon: any }> = [
    { value: 'pending', label: 'Mark as Pending', color: theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600', icon: Clock },
    { value: 'accepted', label: 'Mark as Confirmed', color: theme === 'dark' ? 'text-green-400' : 'text-green-600', icon: Check },
    { value: 'rejected', label: 'Mark as Declined', color: theme === 'dark' ? 'text-red-400' : 'text-red-600', icon: X },
    { value: 'ignored', label: 'Mark as No Response', color: theme === 'dark' ? 'text-gray-400' : 'text-gray-600', icon: UserMinus }
  ];

  const positionClasses = position === 'left' ? 'left-0' : 'right-0';
  const alignClasses = align === 'top' 
    ? 'bottom-full mb-1' 
    : 'top-full mt-1';

  return (
    <div className={`absolute ${positionClasses} ${alignClasses} w-48 rounded-lg shadow-lg border py-1 z-20 ${
      theme === 'dark' 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    }`}>
      {statusOptions.map((option) => {
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            onClick={() => {
              onStatusChange(option.value);
              onClose();
            }}
            disabled={currentStatus === option.value}
            className={`flex items-center w-full px-3 py-2 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${option.color} ${
              theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
            }`}
          >
            <Icon className="h-4 w-4 mr-2" />
            {option.label}
            {currentStatus === option.value && (
              <Check className={`h-3 w-3 ml-auto ${
                theme === 'dark' ? 'text-green-400' : 'text-green-600'
              }`} />
            )}
          </button>
        );
      })}
    </div>
  );
};

export const GuestCard: React.FC<GuestCardProps> = ({
  guest,
  eventId,
  onStatusUpdate,
  onDelete,
  onSelect,
  onEditCompanions,
  onEditComments,
  onSendReminder,
  onError,
  selected = false,
  compact = false,
  eventHasEnded = false
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [showActions, setShowActions] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [copyingLink, setCopyingLink] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [history, setHistory] = useState<GuestEventHistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const [statusMenuPosition, setStatusMenuPosition] = useState<{ align: 'top' | 'bottom'; position: 'left' | 'right' }>({ align: 'bottom', position: 'right' });
  const statusMenuRef = useRef<HTMLDivElement>(null);
  const statusButtonRef = useRef<HTMLButtonElement>(null);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const actionsButtonRef = useRef<HTMLButtonElement>(null);

  // Fetch history when modal opens
  useEffect(() => {
    if (showHistoryModal && history.length === 0 && !loadingHistory) {
      setLoadingHistory(true);
      guestAPI.getGuestHistory(eventId, guest.id)
        .then((data: GuestEventHistoryEntry[]) => {
          setHistory(data);
          setLoadingHistory(false);
        })
        .catch((error: any) => {
          logError('Error fetching guest history', error);
          setLoadingHistory(false);
        });
    }
  }, [showHistoryModal, eventId, guest.id, history.length, loadingHistory]);

  // Calculate dropdown position to prevent cutoff
  useEffect(() => {
    if (showActions && actionsButtonRef.current) {
      const buttonRect = actionsButtonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const dropdownHeight = 300; // Approximate dropdown height
      
      // If not enough space below but enough space above, position above
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }
  }, [showActions]);

  // Calculate status menu position to prevent cutoff
  useEffect(() => {
    if (showStatusMenu && statusButtonRef.current) {
      const buttonRect = statusButtonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const spaceRight = viewportWidth - buttonRect.right;
      const spaceLeft = buttonRect.left;
      const dropdownHeight = 200; // Approximate dropdown height (4 items)
      const dropdownWidth = 192; // w-48 = 12rem = 192px
      
      // Determine vertical alignment
      const align = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight ? 'top' : 'bottom';
      
      // Determine horizontal alignment - prefer right, but use left if not enough space
      const position = spaceRight < dropdownWidth && spaceLeft > dropdownWidth ? 'left' : 'right';
      
      setStatusMenuPosition({ align, position });
    }
  }, [showStatusMenu]);

  const formatCreationDate = (dateString?: string) => {
    if (!dateString) return 'Unknown date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCreatedByName = () => {
    if (guest.created_by) {
      if (typeof guest.created_by === 'object') {
        if (guest.created_by.first_name && guest.created_by.last_name) {
          return `${guest.created_by.first_name} ${guest.created_by.last_name}`;
        }
        return guest.created_by.email || guest.created_by.phone || 'Unknown';
      }
      return 'Unknown';
    }
    return 'Unknown';
  };

  // Use guest_name from API response (now includes nickname if available)
  // Fallback to useGuestDisplayName hook only if guest_name is not available
  const { displayName } = useGuestDisplayName(
    (!guest.guest_name && guest.guest?.id && typeof guest.guest.id === 'string') 
      ? guest.guest.id 
      : undefined
  );
  
  const guestName = formatGuestEventDisplayName(guest, displayName || undefined);
  
  const hasContact = guest.guest?.phone || guest.guest?.email;


  const handleStatusUpdate = (newStatus: GuestEvent['status']) => {
    onStatusUpdate(guest.id, newStatus);
  };

  const handleDelete = () => {
    onDelete(guest.id, guestName);
    setShowActions(false);
  };

  const handleCopyInvitationLink = async () => {
    try {
      setCopyingLink(true);
      setCopySuccess(false);
      
      const response = await guestAPI.getGuestInvitationLink(eventId, guest.id.toString());
      
      // Copy to clipboard with fallback for non-secure contexts
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(response.invitation_link);
        } else {
          // Fallback for older browsers or non-secure contexts
          const textArea = document.createElement('textarea');
          textArea.value = response.invitation_link;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          const successful = document.execCommand('copy');
          textArea.remove();
          
          if (!successful) {
            throw new Error('Fallback copy method failed');
          }
        }
      } catch (clipboardError) {
        // If clipboard API fails, try fallback
        const textArea = document.createElement('textarea');
        textArea.value = response.invitation_link;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        textArea.remove();
        
        if (!successful) {
          throw new Error('All copy methods failed');
        }
      }
      
      setCopySuccess(true);
      setShowActions(false);
      
      // Reset success state after 2 seconds
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
      
    } catch (error) {
      logError('Failed to copy invitation link', error);
      const errorMessage = error instanceof Error 
        ? `Failed to copy invitation link: ${error.message}`
        : 'Failed to copy invitation link. Please try again.';
      
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setCopyingLink(false);
    }
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (statusMenuRef.current && !statusMenuRef.current.contains(target)) {
        setShowStatusMenu(false);
      }
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(target) &&
          actionsButtonRef.current && !actionsButtonRef.current.contains(target)) {
        setShowActions(false);
      }
    };

    if (showStatusMenu || showActions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusMenu, showActions]);

  if (compact) {
    return (
      <div className={`border rounded-lg p-3 sm:p-4 hover:shadow-md transition-all duration-200 ${
        theme === 'dark' 
          ? `bg-gray-800 border-gray-700 ${selected ? 'ring-2 ring-blue-500 border-blue-400' : ''}`
          : `bg-white border-gray-200 ${selected ? 'ring-2 ring-blue-500 border-blue-200' : ''}`
      }`}>
        {/* Mobile: Stack vertically, Desktop: Horizontal layout */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          {/* Left Section */}
          <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
            {onSelect && (
              <input
                type="checkbox"
                checked={selected}
                disabled={eventHasEnded}
                onChange={(e) => {
                  if (eventHasEnded) return;
                  // console.log('🎯 GUEST CARD SELECT DEBUG:', {
                  //   guest_id: guest.id,
                  //   user_id: guest.guest.id,
                  //   selected: e.target.checked,
                  //   guest_name: guest.guest.first_name + ' ' + guest.guest.last_name
                  // });
                  onSelect(guest.id.toString(), e.target.checked);
                }}
                className={`h-4 w-4 text-blue-600 rounded focus:ring-blue-500 flex-shrink-0 mt-0.5 sm:mt-0 ${
                  eventHasEnded 
                    ? 'opacity-50 cursor-not-allowed' 
                    : ''
                } ${
                  theme === 'dark' ? 'border-gray-600' : 'border-gray-300'
                }`}
                title={eventHasEnded ? "Cannot select guests for events that have already ended" : ""}
              />
            )}
            <div className="flex-1 min-w-0">
              {/* Name and Seat */}
              <div className="flex items-center gap-2 flex-wrap mb-1.5 sm:mb-1">
                <h3 className={`font-medium text-sm sm:text-base truncate ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`} title={guestName}>
                  {guestName}
                </h3>
                {guest.seat && (
                  <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border flex-shrink-0 ${
                    theme === 'dark' 
                      ? 'bg-purple-900/30 text-purple-300 border-purple-700/50' 
                      : 'bg-purple-100 text-purple-800 border-purple-200'
                  }`}>
                    🪑 {guest.seat}
                  </span>
                )}
              </div>
              
              {/* Contact Info - Stack on mobile */}
              {(guest.guest?.phone || guest.guest?.email) && (
                <div className="space-y-1 sm:space-y-0 sm:flex sm:items-center sm:gap-2 mb-1.5 sm:mb-1">
                  {guest.guest?.phone && (
                    <p className={`text-xs sm:text-sm ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      📞 {guest.guest.phone}
                    </p>
                  )}
                  {guest.guest?.email && (
                    <p className={`text-xs sm:text-sm ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      ✉️ {guest.guest.email}
                    </p>
                  )}
                </div>
              )}
              
              {/* Creation Info */}
              {(guest.created_by || guest.created_at) && (
                <p className={`text-xs ${
                  theme === 'dark' ? 'text-gray-500' : 'text-gray-600'
                }`}>
                  <User className="inline h-3 w-3 mr-1" />
                  Added by {getCreatedByName()} on {formatCreationDate(guest.created_at)}
                </p>
              )}
            </div>
          </div>
          
          {/* Right Section - Status Badges */}
          <div className="flex items-center gap-2 flex-shrink-0 sm:flex-col sm:items-end">
            <div className="relative flex items-center gap-2">
              <StatusBadge 
                ref={statusButtonRef}
                status={guest.status} 
                onClick={() => setShowStatusMenu(!showStatusMenu)}
              />
              <WhatsAppBadge 
                whatsappStatus={guest.whatsapp_status}
                whatsappSentAt={guest.whatsapp_sent_at}
              />
              <ImageMessageBadge imageMessageStatus={guest.image_message_status} />
              {showStatusMenu && (
                <div ref={statusMenuRef} className="absolute">
                  <StatusUpdateMenu
                    currentStatus={guest.status}
                    onStatusChange={handleStatusUpdate}
                    onClose={() => setShowStatusMenu(false)}
                    position={statusMenuPosition.position}
                    align={statusMenuPosition.align}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`border rounded-lg shadow-sm hover:shadow-md transition-all duration-200 ${
      theme === 'dark' 
        ? `bg-gray-800 border-gray-700 ${selected ? 'ring-2 ring-blue-500 border-blue-400' : ''}`
        : `bg-white border-gray-200 ${selected ? 'ring-2 ring-blue-500 border-blue-300' : ''}`
    }`}>
      {/* Header */}
      <div className={`p-3 border-b ${
        theme === 'dark' ? 'border-gray-700' : 'border-gray-100'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            {onSelect && (
              <input
                type="checkbox"
                checked={selected}
                disabled={eventHasEnded}
                onChange={(e) => {
                  if (eventHasEnded) return;
                  // console.log('🎯 GUEST CARD SELECT DEBUG:', {
                  //   guest_id: guest.id,
                  //   user_id: guest.guest.id,
                  //   selected: e.target.checked,
                  //   guest_name: guest.guest.first_name + ' ' + guest.guest.last_name
                  // });
                  onSelect(guest.id.toString(), e.target.checked);
                }}
                className={`h-4 w-4 text-blue-600 rounded focus:ring-blue-500 flex-shrink-0 ${
                  eventHasEnded 
                    ? 'opacity-50 cursor-not-allowed' 
                    : ''
                } ${
                  theme === 'dark' ? 'border-gray-600' : 'border-gray-300'
                }`}
                title={eventHasEnded ? "Cannot select guests for events that have already ended" : ""}
              />
            )}
            <div className="flex-1 min-w-0">
              <h3 className={`font-medium truncate ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`} title={guestName}>
                {guestName}
              </h3>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                {guest.seat && (
                  <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${
                    theme === 'dark' 
                      ? 'bg-purple-900/30 text-purple-300 border-purple-700/50' 
                      : 'bg-purple-100 text-purple-800 border-purple-200'
                  }`}>
                    🪑 {guest.seat}
                  </span>
                )}
                {guest.is_imported ? (
                  <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
                    theme === 'dark' 
                      ? 'bg-blue-900/30 text-blue-300' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    Imported
                  </span>
                ) : (
                  <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
                    theme === 'dark' 
                      ? 'bg-green-900/30 text-green-300' 
                      : 'bg-green-100 text-green-700'
                  }`}>
                    Manual
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center space-x-1 flex-shrink-0">
            <div className="relative">
              <button
                ref={actionsButtonRef}
                onClick={() => setShowActions(!showActions)}
                className={`p-1 rounded transition-colors ${
                  theme === 'dark' 
                    ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
              >
                <MoreVertical className="h-4 w-4" />
              </button>
              
              {showActions && (
                <div 
                  ref={actionsMenuRef} 
                  className={`absolute ${dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'} right-0 w-40 rounded-lg shadow-xl border py-1 z-[100] ${
                    theme === 'dark' 
                      ? 'bg-gray-800 border-gray-700' 
                      : 'bg-white border-gray-200'
                  }`}
                >
                  <button
                    onClick={() => {
                      setShowStatusMenu(true);
                      setShowActions(false);
                    }}
                    className={`flex items-center w-full px-3 py-2 text-sm transition-colors ${
                      theme === 'dark' 
                        ? 'text-gray-300 hover:bg-gray-700' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Update Status
                  </button>
                  {onSendReminder && (
                    <button
                      onClick={() => {
                        onSendReminder(guest.id.toString());
                        setShowActions(false);
                      }}
                      className={`flex items-center w-full px-3 py-2 text-sm transition-colors ${
                        theme === 'dark' 
                          ? 'text-purple-400 hover:bg-purple-900/20' 
                          : 'text-purple-600 hover:bg-purple-50'
                      }`}
                    >
                      <Bell className="h-4 w-4 mr-2" />
                      Reminder
                    </button>
                  )}
                  {onEditComments && (
                    <button
                      onClick={() => onEditComments(guest)}
                      className={`flex items-center w-full px-3 py-2 text-sm transition-colors ${
                        theme === 'dark' 
                          ? 'text-indigo-400 hover:bg-indigo-900/20' 
                          : 'text-indigo-600 hover:bg-indigo-50'
                      }`}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      {(guest.host_comments || guest.admin_comments) ? 'Edit Comments' : 'Add Comments'}
                    </button>
                  )}
                  <button
                    onClick={handleCopyInvitationLink}
                    disabled={copyingLink}
                    className={`flex items-center w-full px-3 py-2 text-sm transition-colors ${
                      copyingLink 
                        ? 'opacity-50 cursor-not-allowed' 
                        : theme === 'dark' 
                          ? 'text-blue-400 hover:bg-blue-900/20' 
                          : 'text-blue-600 hover:bg-blue-50'
                    }`}
                  >
                    <Copy className={`h-4 w-4 mr-2 ${copySuccess ? 'text-green-500' : ''}`} />
                    {copyingLink ? 'Copying...' : copySuccess ? 'Copied!' : 'Copy invitation link'}
                  </button>
                  <button
                    onClick={() => {
                      setShowHistoryModal(true);
                      setShowActions(false);
                    }}
                    className={`flex items-center w-full px-3 py-2 text-sm transition-colors ${
                      theme === 'dark' 
                        ? 'text-blue-400 hover:bg-blue-900/20' 
                        : 'text-blue-600 hover:bg-blue-50'
                    }`}
                  >
                    <History className="h-4 w-4 mr-2" />
                    History
                  </button>
                  <button
                    onClick={handleDelete}
                    className={`flex items-center w-full px-3 py-2 text-sm transition-colors ${
                      theme === 'dark' 
                        ? 'text-red-400 hover:bg-red-900/20' 
                        : 'text-red-600 hover:bg-red-50'
                    }`}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Status */}
        <div className="mb-3">
          <div className="relative">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusBadge 
                ref={statusButtonRef}
                status={guest.status} 
                onClick={() => setShowStatusMenu(!showStatusMenu)}
              />
              <WhatsAppBadge 
                whatsappStatus={guest.whatsapp_status}
                whatsappSentAt={guest.whatsapp_sent_at}
              />
              <ImageMessageBadge imageMessageStatus={guest.image_message_status} />
              {(guest.host_comments || guest.admin_comments) && (
                <div className="relative group">
                  <button
                    onClick={() => onEditComments && onEditComments(guest)}
                    className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs cursor-pointer transition-all duration-200 hover:scale-105 ${
                      theme === 'dark'
                        ? 'bg-indigo-900/30 text-indigo-300 border border-indigo-700/50 hover:bg-indigo-800/50'
                        : 'bg-indigo-100 text-indigo-700 border border-indigo-200 hover:bg-indigo-200'
                    }`}
                  >
                    <FileText className="h-3 w-3" />
                    <span>Notes</span>
                  </button>
                  
                  {/* Tooltip on hover */}
                  <div className={`absolute z-50 hidden group-hover:block bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-3 rounded-lg shadow-lg border ${
                    theme === 'dark'
                      ? 'bg-gray-800 border-gray-700'
                      : 'bg-white border-gray-200'
                  }`}>
                    <div className={`text-xs font-semibold mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Guest Notes
                    </div>
                    {(guest.host_comments || guest.admin_comments) && (
                      <>
                        {(guest.host_comments && (
                          <div className="mb-2">
                            <div className={`text-xs font-medium mb-1 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-600'}`}>
                              Host Notes:
                            </div>
                            <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                              {guest.host_comments.length > 100 
                                ? guest.host_comments.substring(0, 100) + '...' 
                                : guest.host_comments}
                            </div>
                            {guest.host_comments_updated_at && (
                              <div className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                                Updated {new Date(guest.host_comments_updated_at).toLocaleDateString()}
                                {guest.host_comments_updated_by_name && ` by ${guest.host_comments_updated_by_name}`}
                              </div>
                            )}
                          </div>
                        ))}
                        {(guest.admin_comments && (
                          <div>
                            {guest.host_comments && <div className={`border-t my-2 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}></div>}
                            <div className={`text-xs font-medium mb-1 ${theme === 'dark' ? 'text-indigo-300' : 'text-indigo-600'}`}>
                              Admin Notes:
                            </div>
                            <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                              {guest.admin_comments.length > 100 
                                ? guest.admin_comments.substring(0, 100) + '...' 
                                : guest.admin_comments}
                            </div>
                            {guest.admin_comments_updated_at && (
                              <div className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                                Updated {new Date(guest.admin_comments_updated_at).toLocaleDateString()}
                                {guest.admin_comments_updated_by_name && ` by ${guest.admin_comments_updated_by_name}`}
                              </div>
                            )}
                          </div>
                        ))}
                      </>
                    )}
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                      <div className={`border-4 border-transparent ${
                        theme === 'dark'
                          ? 'border-t-gray-800'
                          : 'border-t-white'
                      }`}></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {showStatusMenu && (
              <div ref={statusMenuRef} className="relative">
                <StatusUpdateMenu
                  currentStatus={guest.status}
                  onStatusChange={handleStatusUpdate}
                  onClose={() => setShowStatusMenu(false)}
                  position={statusMenuPosition.position}
                  align={statusMenuPosition.align}
                />
              </div>
            )}
          </div>
        </div>

        {/* Contact Information */}
        {hasContact && (
          <div className="space-y-2 mb-3">
            {guest.guest?.phone && (
              <div className={`flex items-center text-sm ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                <Phone className={`h-3 w-3 mr-2 flex-shrink-0 ${
                  theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                }`} />
                <a 
                  href={`tel:${guest.guest.phone}`} 
                  className={`transition-colors truncate ${
                    theme === 'dark' ? 'hover:text-blue-400' : 'hover:text-blue-600'
                  }`}
                  title={guest.guest.phone}
                >
                  {guest.guest.phone}
                </a>
              </div>
            )}
            {guest.guest?.email && (
              <div className={`flex items-center text-sm ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                <Mail className={`h-3 w-3 mr-2 flex-shrink-0 ${
                  theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                }`} />
                <a 
                  href={`mailto:${guest.guest.email}`} 
                  className={`transition-colors truncate ${
                    theme === 'dark' ? 'hover:text-blue-400' : 'hover:text-blue-600'
                  }`}
                  title={guest.guest.email}
                >
                  {guest.guest.email}
                </a>
              </div>
            )}
          </div>
        )}

        {/* Companion Information */}
        {onEditCompanions && (
          <div className={`rounded p-2 mb-3 ${
            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
          }`}>
            <div className={`flex items-center justify-between text-sm ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              <div className="flex items-center flex-1">
                <Users className={`h-4 w-4 mr-2 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`} />
                <div className="flex flex-col">
                  {guest.allow_companions && (() => {
                    const detailedCount = guest.companion_guest_events?.length || 0;
                    const emptyCount = guest.empty_companion_count || 0;
                    const totalCount = detailedCount + emptyCount;
                    
                    if (totalCount === 0) {
                      return <span className="font-medium">0 companions</span>;
                    }
                    
                    const parts: string[] = [];
                    if (detailedCount > 0) {
                      parts.push(`${detailedCount} detailed`);
                    }
                    if (emptyCount > 0) {
                      parts.push(`${emptyCount} empty`);
                    }
                    
                    return (
                      <span className="font-medium">
                        {totalCount} companion{totalCount > 1 ? 's' : ''} ({parts.join(', ')})
                      </span>
                    );
                  })()}
                  {/* Show companion RSVP status summary */}
                  {guest.allow_companions && (() => {
                    const detailedCompanions = guest.companion_guest_events || [];
                    const emptyStatuses = guest.empty_companion_statuses || [];
                    const emptyCount = guest.empty_companion_count || 0;
                    
                    const detailedAccepted = detailedCompanions.filter(c => c.status === 'accepted').length;
                    const detailedRejected = detailedCompanions.filter(c => c.status === 'rejected').length;
                    const detailedPending = detailedCompanions.filter(c => c.status === 'pending').length;
                    
                    const emptyAccepted = emptyStatuses.filter(s => s.status === 'accepted').length;
                    const emptyRejected = emptyStatuses.filter(s => s.status === 'rejected').length;
                    const emptyPending = emptyStatuses.filter(s => s.status === 'pending').length;
                    
                    const totalAccepted = detailedAccepted + emptyAccepted;
                    const totalRejected = detailedRejected + emptyRejected;
                    const totalPending = detailedPending + emptyPending;
                    
                    if (totalAccepted + totalRejected + totalPending > 0) {
                      const statusParts: string[] = [];
                      if (totalAccepted > 0) statusParts.push(`${totalAccepted} accepted`);
                      if (totalRejected > 0) statusParts.push(`${totalRejected} declined`);
                      if (totalPending > 0) statusParts.push(`${totalPending} pending`);
                      
                      return (
                        <div className={`text-xs mt-1 ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {statusParts.join(', ')}
                          {emptyCount > 0 && (
                            <span className="ml-1 opacity-75">
                              ({emptyAccepted + emptyRejected + emptyPending} empty)
                            </span>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
              <button
                onClick={() => !eventHasEnded && onEditCompanions(guest.id, guest)}
                disabled={eventHasEnded}
                className={`p-1 rounded-md transition-all duration-200 ${
                  eventHasEnded
                    ? 'opacity-50 cursor-not-allowed text-gray-400'
                    : theme === 'dark' 
                      ? 'text-purple-400 hover:text-purple-300 hover:bg-purple-900/30' 
                      : 'text-purple-600 hover:text-purple-800 hover:bg-purple-50'
                }`}
                title={eventHasEnded ? "Cannot edit companions for events that have already ended" : "Edit companions"}
              >
                <Edit3 className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}

        {/* Invitation Date */}
        <div className={`text-xs border-t pt-2 ${
          theme === 'dark' 
            ? 'text-gray-400 border-gray-700' 
            : 'text-gray-500 border-gray-100'
        }`}>
          <div className="flex items-center">
            <Calendar className={`h-3 w-3 mr-1 ${
              theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
            }`} />
            {guest.invite_sent_at ? (
              <span>Invited {new Date(guest.invite_sent_at).toLocaleDateString()}</span>
            ) : (
              <span>Not invited yet</span>
            )}
          </div>
        </div>
      </div>

      {/* History Modal */}
      <HistoryModal
        isOpen={showHistoryModal}
        onClose={() => {
          setShowHistoryModal(false);
        }}
        title={`Change History for ${guestName}`}
        history={history}
        loading={loadingHistory}
      />
    </div>
  );
};
